import React, { useMemo, useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { useProfile } from '../../../context/ProfileContext';
import SongDetailChart from '../../../components/artistDashboard/SongDetailChart';
import NuclearAwardInfoModal from '../../../components/artistDashboard/NuclearAwardInfoModal'; // ✅ Import standalone modal
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';

import radiumIcon from '../../../assets/radium.png';
import plutoniumIcon from '../../../assets/plutonium.png';
import uraniumIcon from '../../../assets/uranium.png';

// Register ChartJS modules
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const AudienceDashboard = ({ summaryData, monthKeys, songListData }) => {
  const { isDarkMode } = useProfile();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNuclearModalOpen, setIsNuclearModalOpen] = useState(false);

  // Search Functionality States
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSong, setSelectedSong] = useState(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  // Sync Search Query locally against passed-down song list
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      return;
    }
    const results = songListData?.songs.filter(song =>
      (song.title || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    setSearchResults(results || []);
  }, [searchTerm, songListData]);

  const handleSelectSong = (song) => {
    setSelectedSong(song);
    setSearchTerm(song.title);
    setSearchResults([]);
    setIsDropdownVisible(false);
  };

  const clearSelectedSong = () => {
    setSelectedSong(null);
    setSearchTerm('');
  };

  // Pre-sort songs by Certified Downloads for our new list
  const songsByNuclearCertification = useMemo(() => {
    if (!songListData?.songs) return [];
    return [...songListData.songs].sort((a, b) => {
      const downloadsA = a.stats?.totalDownloads || a.downloads || 0;
      const downloadsB = b.stats?.totalDownloads || b.downloads || 0;
      return downloadsB - downloadsA;
    });
  }, [songListData]);

  const chartInfo = useMemo(() => {
    if (!monthKeys || !summaryData) {
      return { labels: [], datasets: [], suggestedMax: 10 };
    }

    // Dynamic window offset checks
    const firstDataIndex = monthKeys.findIndex(key => 
      (summaryData[`monthlyTotals.${key}`] || 0) > 0
    );

    let labels = [];

    if (firstDataIndex === -1) {
      labels = monthKeys.slice(-6);
    } else {
      const startIndex = Math.max(0, firstDataIndex - 1);
      labels = monthKeys.slice(startIndex);

      if (labels.length < 6) {
        const needed = 6 - labels.length;
        const lastMonthString = labels[labels.length - 1];
        const [month, year] = lastMonthString.split('-').map(Number);
        const lastDate = new Date(year, month - 1); 

        for (let i = 0; i < needed; i++) {
          lastDate.setMonth(lastDate.getMonth() + 1);
          const nextMonth = (lastDate.getMonth() + 1).toString().padStart(2, '0');
          const nextYear = lastDate.getFullYear();
          labels.push(`${nextMonth}-${nextYear}`);
        }
      }
    }

    const leisureData = [];
    const discoveryData = [];

    labels.forEach((key, index) => {
      const totalStreams = summaryData[`monthlyTotals.${key}`] !== undefined 
        ? summaryData[`monthlyTotals.${key}`] 
        : 0;
      const discoveryStreams = summaryData[`monthlyTrendsTotals.${key}`];
      const hasData = totalStreams !== undefined || discoveryStreams !== undefined;

      if (hasData) {
        const total = totalStreams || 0;
        const discovery = discoveryStreams || 0;
        leisureData.push(total - discovery);
        discoveryData.push(discovery);
      } else if (index === 0) {
        leisureData.push(0);
        discoveryData.push(0);
      } else {
        leisureData.push(null);
        discoveryData.push(null);
      }
    });
    
    const maxValue = Math.max(...leisureData, ...discoveryData);

    return {
      labels: labels,
      datasets: [
        {
          label: 'Discovery Streams',
          data: discoveryData,
          borderColor: 'rgb(249, 115, 22)', // Balanced neon orange
          backgroundColor: 'rgb(249, 115, 22)',
          tension: 0.2,
        },
        {
          label: 'Leisure Streams',
          data: leisureData,
          borderColor: 'rgb(59, 130, 246)', // Vibrant sky blue
          backgroundColor: 'rgb(59, 130, 246)',
          tension: 0.2,
        },
      ],
      suggestedMax: maxValue < 10 ? 10 : maxValue + 5,
    };
  }, [summaryData, monthKeys]);

  // Chart configuration with clean dark mode color updates
  const chartOptions = useMemo(() => {
    const gridColor = isDarkMode ? 'rgba(63, 63, 70, 0.4)' : 'rgba(228, 228, 231, 0.8)';
    const textColor = isDarkMode ? '#a1a1aa' : '#71717a';

    return {
      responsive: true,
      maintainAspectRatio: false,
      resizeDelay: 0,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            color: textColor,
            font: { weight: 'bold', size: 11 }
          },
        },
      },
      scales: {
        x: {
          grid: { color: gridColor },
          ticks: { color: textColor }
        },
        y: {
          beginAtZero: true,
          suggestedMax: chartInfo.suggestedMax,
          grid: { color: gridColor },
          ticks: { 
            precision: 0,
            color: textColor
          }
        }
      }
    };
  }, [isDarkMode, chartInfo.suggestedMax]);

  const chartData = {
    labels: chartInfo.labels,
    datasets: chartInfo.datasets,
  };

  const getNuclearIcon = (downloads) => {
    if (downloads >= 100) return uraniumIcon;
    if (downloads >= 50) return plutoniumIcon;
    if (downloads >= 10) return radiumIcon;
    return null;
  };

  // If there's no stream data at all
  if (!summaryData || !summaryData.allTimeStreams || summaryData.allTimeStreams === 0) {
    return (
      <div className={`text-center py-10 p-4 sm:p-6 rounded-2xl border ${
        isDarkMode ? "bg-zinc-900 border-zinc-850 text-zinc-400" : "bg-white border-gray-200 text-gray-500"
      }`}>
        <p className="text-sm opacity-60">No stream analytics available yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* 1. DYNAMIC SONG SEARCH BAR BAR */}
      <div className="relative">
        <div className="relative flex items-center">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsDropdownVisible(true);
            }}
            onFocus={() => setIsDropdownVisible(true)}
            onBlur={() => setTimeout(() => setIsDropdownVisible(false), 200)}
            placeholder="Search catalog track for granular stats..."
            className={`w-full p-3.5 pl-11 pr-10 border rounded-xl text-sm focus:outline-none transition-all ${
              isDarkMode 
                ? "bg-zinc-900 border-zinc-850 text-white focus:border-brand-pink placeholder-zinc-500" 
                : "bg-white border-gray-200 text-gray-800 focus:border-blue-500 placeholder-gray-400"
            }`}
          />
          {/* Magnifying Glass Search Icon */}
          <div className="absolute left-4">
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {/* Close Clear Search button */}
          {searchTerm && (
            <button 
              onClick={clearSelectedSong}
              className="absolute right-4 text-zinc-500 hover:text-brand-pink transition-colors"
            >
              <span className="text-xl">×</span>
            </button>
          )}
        </div>

        {/* Dynamic Search Dropdown Result Drawer */}
          {isDropdownVisible && searchResults.length > 0 && (
          <div className={`absolute top-full left-0 right-0 border rounded-xl mt-1.5 shadow-2xl z-50 max-h-60 overflow-y-auto ${
            isDarkMode 
              ? "bg-zinc-800 border-zinc-700 divide-y divide-zinc-700/50" // ✅ Lighter slate-gray for dark mode contrast
              : "bg-white border-gray-200 divide-y divide-gray-100"
          }`}>
            {searchResults.map(song => (
              <div
                key={song.id}
                onMouseDown={() => handleSelectSong(song)}
                className={`p-3.5 text-sm cursor-pointer font-medium transition-colors ${
                  isDarkMode ? "text-zinc-200 hover:bg-zinc-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {song.title}
              </div>
            ))}
          </div>
        )}  
      </div>

      {/* DETAILED / GENERAL PANEL DISPLAY */}
      {!selectedSong ? (
        <div className="space-y-6">
          {/* Main Line Chart */}
          <div className={`p-4 sm:p-6 rounded-2xl border transition-all ${
            isDarkMode ? "bg-zinc-900 border-zinc-850" : "bg-white border-gray-150"
          }`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-sm font-extrabold uppercase tracking-widest opacity-60">All Streams (Last 12 Months)</h2>
              <button 
                onClick={() => setIsModalOpen(true)} 
                className={`p-1.5 rounded-lg border transition-all ${
                  isDarkMode ? "border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800" : "border-gray-100 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>
            <div className="h-80 w-full relative flex-grow">
              <Line options={chartOptions} data={chartData} />
            </div>
          </div>

          {/* NUCLEAR CERTIFICATION RANKINGS SECTION */}
          <div className={`p-5 rounded-2xl border ${
            isDarkMode ? "bg-zinc-900 border-zinc-850" : "bg-white border-gray-150"
          }`}>
            <div className="flex justify-between items-center mb-5">
              <div>
                <h3 className="text-sm font-extrabold uppercase tracking-widest opacity-60">Nuclear Certifications</h3>
                <p className="text-xs opacity-45 mt-0.5">Top downloaded songs in your catalog</p>
              </div>
              
              <button 
                onClick={() => setIsNuclearModalOpen(true)}
                className={`p-2 rounded-xl border flex items-center space-x-1.5 transition-all text-xs font-bold ${
                  isDarkMode 
                    ? "border-zinc-800 bg-zinc-950 text-zinc-300 hover:text-white hover:bg-zinc-850" 
                    : "border-gray-150 bg-gray-50 text-gray-600 hover:bg-gray-100"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-brand-pink" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                
              </button>
            </div>

            <div className="grid gap-4">
              {songsByNuclearCertification.slice(0, 5).map((song, idx) => {
                const stats = song.stats || {};
                const downloads = stats.totalDownloads || song.downloads || 0;
                
                const tiers = {
                  radium: { name: "Radium", downloads: 10, icon: radiumIcon, color: "bg-green-400 dark:bg-emerald-400" },
                  plutonium: { name: "Plutonium", downloads: 50, icon: plutoniumIcon, color: "bg-yellow-400 dark:bg-amber-400" },
                  uranium: { name: "Uranium", downloads: 100, icon: uraniumIcon, color: "bg-pink-500 dark:bg-brand-pink" },
                };

                let nextAward = tiers.radium;
                let prevDownloads = 0;

                if (downloads >= 10 && downloads < 50) {
                  nextAward = tiers.plutonium;
                  prevDownloads = 10;
                } else if (downloads >= 50 && downloads < 100) {
                  nextAward = tiers.uranium;
                  prevDownloads = 50;
                } else if (downloads >= 100) {
                  nextAward = null;
                }

                const progress = nextAward 
                  ? ((downloads - prevDownloads) / (nextAward.downloads - prevDownloads)) * 100 
                  : 100;

                return (
                  <div 
                    key={song.id || idx} 
                    className={`p-4 rounded-xl border transition-colors ${
                      isDarkMode ? "bg-zinc-950/40 border-zinc-800/50" : "bg-gray-50 border-gray-100"
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`p-1 flex-shrink-0 rounded-lg border ${
                        isDarkMode ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-200"
                      }`}>
                        <img 
                          src={downloads >= 100 ? uraniumIcon : downloads >= 50 ? plutoniumIcon : radiumIcon} 
                          alt="Milestone Icon" 
                          className="w-10 h-10 object-contain" 
                        />
                      </div>

                      <div className="min-w-0 flex-grow">
                        <h4 className={`text-xs font-bold leading-tight truncate ${
                          isDarkMode ? "text-zinc-300" : "text-gray-700"
                        }`}>
                          "{song.title}"
                        </h4>
                        <p className="text-[10px] font-medium opacity-50 mt-0.5">
                          {nextAward 
                            ? `Progress to ${nextAward.name} (${downloads}/${nextAward.downloads} Dwnlds)`
                            : `All Certifications Unlocked! (${downloads} DLs)`
                          }
                        </p>
                      </div>

                      <div className="flex-shrink-0">
                        {downloads >= 100 ? (
                          <span className="text-[8px] font-black uppercase tracking-wider bg-pink-500/10 text-brand-pink px-2 py-0.5 rounded-full border border-brand-pink/20">Uranium</span>
                        ) : downloads >= 50 ? (
                          <span className="text-[8px] font-black uppercase tracking-wider bg-amber-400/10 text-amber-400 px-2 py-0.5 rounded-full border border-amber-400/20">Plutonium</span>
                        ) : downloads >= 10 ? (
                          <span className="text-[8px] font-black uppercase tracking-wider bg-emerald-400/10 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-400/20">Radium</span>
                        ) : (
                          <span className="text-[8px] font-bold uppercase tracking-wider bg-zinc-500/10 text-zinc-500 px-2 py-0.5 rounded-full">Base</span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-[9px] font-black">
                        <span className="opacity-40 uppercase tracking-wider">Milestone Progress</span>
                        <span className={isDarkMode ? "text-brand-pink" : "text-gray-800"}>
                          {Math.floor(progress)}%
                        </span>
                      </div>

                      <div className={`w-full rounded-full h-2.5 p-[2px] ${
                        isDarkMode ? "bg-zinc-800/60" : "bg-gray-200"
                      }`}>
                        <div
                          className={`h-full rounded-full transition-all duration-500 ease-out ${
                            nextAward ? nextAward.color : "bg-gradient-to-r from-green-400 via-yellow-400 to-pink-500"
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

          
      ) : (
        // Render detailed granular song chart if selected
        <div className="relative">
          <SongDetailChart
            song={selectedSong}
            monthKeys={monthKeys}
          />
        </div>
      )}

      {/* 3. INFORMATION MODAL */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black/60 flex justify-center items-center z-[150] p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className={`rounded-2xl shadow-2xl p-6 max-w-md w-full border ${
              isDarkMode ? "bg-zinc-900 border-zinc-800 text-zinc-100" : "bg-white border-gray-100 text-gray-800"
            }`}
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">Stream Types Explained</h3>
            <div className="space-y-4 text-sm opacity-85 leading-relaxed">
              <p>
                <strong className="text-orange-500">Discovery Streams</strong> are plays from sources like the main 'Trends' feed. These streams help new listeners find your music.
              </p>
              <p>
                <strong className="text-blue-500">Leisure Streams</strong> are plays from your existing fans, such as when they play your music from search, your discography, an album, playlist, or their listening history.
              </p>
            </div>
            <button
              onClick={() => setIsModalOpen(false)}
              className="mt-6 w-full bg-brand-pink text-white font-bold py-3 px-4 rounded-xl shadow-lg shadow-brand-pink/20 hover:scale-[1.01] active:scale-95 transition-all duration-200"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <NuclearAwardInfoModal 
        isOpen={isNuclearModalOpen} 
        onClose={() => setIsNuclearModalOpen(false)} 
        isDarkMode={isDarkMode} 
      />
    </div>
  );
};

export default AudienceDashboard;